import taxCreditUrl from '~/constants/common/taxCreditUrl';
import CarIcon from '~/icons/CarIcon';
import { BulletIcon, CheckMarkIcon } from '~/icons/EvModalIcons';
import MoneyIcon from '~/icons/MoneyIcon';
import PersonIcon from '~/icons/PersonIcon';
import {
  Bold,
  NumberedItem,
  NumberedList,
  RequirementsList,
  SectionHeaderSmall
} from './EvTaxModal.styles';

function CustomListItem({
  type,
  children
}: {
  type: 'checkmark' | 'bullet';
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-start gap-8">
      <div className="flex h-[20px] items-center">
        {type === 'checkmark' ? <CheckMarkIcon /> : <BulletIcon />}
      </div>
      {children}
    </li>
  );
}

export const CleanVehicleTaxSavings = () => {
  return (
    <section>
      <div className="mb-16 text-blue-6 tl-body-s m:tl-body-m">
        This vehicle qualifies for a Previously-Owned Clean Vehicle Tax Credit
        up to a maximum amount of <Bold>$4,000</Bold>
      </div>
      <div className="mt-auto text-grey-5 tl-body-xs m:tl-body-m">
        To take advantage of these savings at checkout, you must meet the
        eligibility requirements.
      </div>
    </section>
  );
};

export function IncomeRequirements() {
  return (
    <section className="flex flex-col gap-16 rounded-[6px] bg-grey-1 p-16">
      <SectionHeaderSmall>
        <MoneyIcon /> Income Requirements
      </SectionHeaderSmall>
      <NumberedList>
        <NumberedItem>
          Your modified adjusted gross income for the year in which you purchase
          the vehicle or the prior year does not exceed:
          <RequirementsList>
            <CustomListItem type={'bullet'}>
              $150,000 if you’re married filing jointly or if you’re filing as a
              surviving spouse
            </CustomListItem>
            <CustomListItem type={'bullet'}>
              $112,500 if you’re filing as head of household
            </CustomListItem>
            <CustomListItem type={'bullet'}>
              $75,000 for all other filers (including individuals and if you’re
              married filing separately)
            </CustomListItem>
          </RequirementsList>
        </NumberedItem>
      </NumberedList>
    </section>
  );
}

export function VehicleRequirements() {
  return (
    <section className="flex flex-col gap-16 rounded-[6px] bg-grey-1 p-16">
      <SectionHeaderSmall>
        <CarIcon /> Vehicle Requirements
      </SectionHeaderSmall>
      <RequirementsList>
        <CustomListItem type={'checkmark'}>
          Chargeable battery with a capacity of at least 7 kW/h
        </CustomListItem>
        <CustomListItem type={'checkmark'}>
          Purchase price less than or equal to $25,000
        </CustomListItem>
        <CustomListItem type={'checkmark'}>
          At least 2 model years old
        </CustomListItem>
      </RequirementsList>
    </section>
  );
}

export const AdditionalRequirements = () => {
  return (
    <section className="flex flex-col gap-16 rounded-[6px] bg-grey-1 p-16">
      <SectionHeaderSmall>
        <PersonIcon /> Additional Requirements
      </SectionHeaderSmall>
      <RequirementsList>
        <CustomListItem type={'bullet'}>
          No previously-owned clean vehicle tax credit has been claimed on this
          vehicle by a prior owner
        </CustomListItem>
        <CustomListItem type={'bullet'}>
          You have not claimed the previously-owned clean vehicle tax credit in
          any of the prior 3 years
        </CustomListItem>
        <CustomListItem type={'bullet'}>
          You will not be claimed as a dependent on another person’s tax return
          for the tax year in which you purchase the vehicle
        </CustomListItem>
        <CustomListItem type={'bullet'}>
          You are not purchasing the vehicle for resale or business purposes
        </CustomListItem>
        <CustomListItem type={'bullet'}>
          <div>
            For full eligibility requirements and more information on the
            previously-owned clean vehicle tax credit, please visit{' '}
            <span>
              <a
                href={taxCreditUrl}
                className="text-grey-5 underline"
                data-analytics-id="EV Credit Modal - IRS Link"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.irs.gov/credits-deductions/used-clean-vehicle-credit
              </a>
            </span>
          </div>
        </CustomListItem>
      </RequirementsList>
    </section>
  );
};
